import { get, create, updatePatch, deleteResource } from "./apiService";
import {
  resolveBusinessRoute,
  withBusinessContext,
} from "../utils/businessRoute";

const notificationPath = (suffix: string, businessId?: string | null): string =>
  resolveBusinessRoute(businessId, `/api/v2/notifications${suffix}`);

export interface Notification {
  _id: string;
  type: string;
  title?: string;
  message?: string;
  isRead: boolean;
  read?: boolean;
  createdAt: string;
  deepLink?: {
    screen: string;
    params?: Record<string, string>;
  };
  [key: string]: unknown;
}

export interface NotificationListResponse {
  notifications: Notification[];
  results?: Notification[];
  total: number;
  totalCount?: number;
  totalUnread?: number;
  unreadCount?: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface RegisterTokenPayload {
  provider: "expo" | "webpush";
  subscription?: PushSubscription;
  token: string;
  deviceId?: string;
  platform?: "ios" | "android" | "web";
}

export interface NotificationTypeInfo {
  label: string;
  icon: string;
  category: string;
}

export const NOTIFICATION_CATEGORIES = {
  ALL: "all",
  GENERAL: "general",
  FUNDS: "funds",
  TRADING: "trading",
  FIAT: "fiat",
  PAY: "pay",
  PRICE_ALERT: "price_alert",
  AUTOSTACK: "autostack",
  TRANSACTION: "transaction",
} as const;

export const NOTIFICATION_TYPES: Record<string, NotificationTypeInfo> = {
  funds_sent: { label: "Funds Sent", icon: "send", category: "funds" },
  funds_received: {
    label: "Funds Received",
    icon: "receive",
    category: "funds",
  },
  purchase_confirmed: { label: "Purchase", icon: "buy", category: "trading" },
  sale_complete: { label: "Sale", icon: "sell", category: "trading" },
  deposit_successful: { label: "Deposit", icon: "deposit", category: "fiat" },
  withdrawal_successful: {
    label: "Withdrawal",
    icon: "withdraw",
    category: "fiat",
  },
  payment_sent: { label: "88Pay Sent", icon: "pay_sent", category: "pay" },
  payment_received: {
    label: "88Pay Received",
    icon: "pay_received",
    category: "pay",
  },
  price_alert_up: {
    label: "Price Up",
    icon: "price_up",
    category: "price_alert",
  },
  price_alert_down: {
    label: "Price Down",
    icon: "price_down",
    category: "price_alert",
  },
  transaction_failed: {
    label: "Failed",
    icon: "error",
    category: "transaction",
  },
  autostack_created: {
    label: "AutoStack Created",
    icon: "autostack",
    category: "autostack",
  },
  autostack_triggered: {
    label: "AutoStack Triggered",
    icon: "autostack",
    category: "autostack",
  },
  autostack_failed: {
    label: "AutoStack Failed",
    icon: "autostack_error",
    category: "autostack",
  },
  autostack_edited: {
    label: "AutoStack Edited",
    icon: "autostack",
    category: "autostack",
  },
  autostack_paused: {
    label: "AutoStack Paused",
    icon: "autostack_paused",
    category: "autostack",
  },
  autostack_resumed: {
    label: "AutoStack Resumed",
    icon: "autostack",
    category: "autostack",
  },
  autostack_reminder: {
    label: "AutoStack Reminder",
    icon: "autostack_reminder",
    category: "autostack",
  },
};

export const getNotifications = async (
  page = 1,
  limit = 20,
  category: string | null = null,
  businessId?: string | null
): Promise<NotificationListResponse> => {
  const params: Record<string, unknown> = { page, limit };
  if (category && category !== NOTIFICATION_CATEGORIES.ALL) {
    params.category = category;
  }
  return get<NotificationListResponse>(
    notificationPath("", businessId),
    withBusinessContext(businessId, params)
  );
};

export const getNotification = async (
  id: string,
  businessId?: string | null
): Promise<Notification> => {
  return get<Notification>(
    notificationPath(`/${id}`, businessId),
    withBusinessContext(businessId)
  );
};

export const markAsReadService = async (
  notificationId: string,
  businessId?: string | null
): Promise<unknown> => {
  return updatePatch(
    notificationPath(`/${notificationId}/read`, businessId),
    withBusinessContext(businessId)
  );
};

export const markAllAsReadService = async (
  businessId?: string | null
): Promise<unknown> => {
  return updatePatch(
    notificationPath("/read-all", businessId),
    withBusinessContext(businessId)
  );
};

export const deleteNotificationService = async (
  notificationId: string,
  businessId?: string | null
): Promise<unknown> => {
  return deleteResource(
    notificationPath(`/${notificationId}`, businessId),
    withBusinessContext(businessId)
  );
};

export const deleteNotificationsAll = async (
  businessId?: string | null
): Promise<unknown> => {
  return deleteResource(
    notificationPath("/clear-all", businessId),
    withBusinessContext(businessId)
  );
};

interface WebPushPublicKeyResponse {
  publicKey: string;
}

export const getWebPushPublicKey = async (): Promise<string> => {
  const response = await get<WebPushPublicKeyResponse>(
    "/api/v2/notifications/web-push-public-key"
  );
  return response.publicKey;
};

export const registerPushToken = async (
  token: string,
  provider: RegisterTokenPayload["provider"],
  deviceId?: string,
  platform?: RegisterTokenPayload["platform"]
): Promise<unknown> => {
  const payload: RegisterTokenPayload = { provider, token };
  if (deviceId) payload.deviceId = deviceId;
  if (platform) payload.platform = platform;
  return create("/api/v2/notifications/register-token", payload);
};

export const unregisterPushToken = async (
  token: string,
  provider: RegisterTokenPayload["provider"]
): Promise<unknown> => {
  return deleteResource(
    "/api/v2/notifications/unregister-token",
    {},
    { provider, token }
  );
};

export const registerWebPushSubscription = async (
  subscription: PushSubscription
): Promise<unknown> => {
  return create("/api/v2/notifications/register-token", {
    provider: "webpush",
    subscription,
  });
};

export const unregisterWebPushSubscription = async (
  subscription: PushSubscription
): Promise<unknown> => {
  return deleteResource(
    "/api/v2/notifications/unregister-token",
    {},
    {
      provider: "webpush",
      subscription,
    }
  );
};

const NOTIFICATION_TYPES_MAP = new Map<string, NotificationTypeInfo>(
  Object.entries(NOTIFICATION_TYPES)
);

export const getNotificationTypeInfo = (type: string): NotificationTypeInfo => {
  const defaultInfo: NotificationTypeInfo = {
    label: "Notification",
    icon: "default",
    category: "general",
  };
  return NOTIFICATION_TYPES_MAP.get(type) ?? defaultInfo;
};

export interface FormattedNotification extends Notification {
  displayLabel: string;
  displayIcon: string;
  displayCategory: string;
  formattedDate: string;
}

export const formatNotification = (
  notification: Notification
): FormattedNotification => {
  const typeInfo = getNotificationTypeInfo(notification.type);
  return {
    ...notification,
    displayLabel: typeInfo.label,
    displayIcon: typeInfo.icon,
    displayCategory: typeInfo.category,
    formattedDate: formatNotificationDate(notification.createdAt),
  };
};

export const formatNotificationDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  const MS_PER_MINUTE = 60 * 1000;
  const MS_PER_HOUR = 60 * MS_PER_MINUTE;
  const MS_PER_DAY = 24 * MS_PER_HOUR;

  const diffMins = Math.floor(diffMs / MS_PER_MINUTE);
  const diffHours = Math.floor(diffMs / MS_PER_HOUR);
  const diffDays = Math.floor(diffMs / MS_PER_DAY);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

export const getUnreadCount = async (
  businessId?: string | null
): Promise<number> => {
  try {
    const result = await getNotifications(1, 1, null, businessId);
    return result?.totalUnread ?? result?.unreadCount ?? 0;
  } catch {
    return 0;
  }
};

export const handleNotificationDeepLink = (
  notification: Notification,
  navigate: (path: string) => void
): void => {
  if (!notification.deepLink?.screen) return;

  const { screen, params } = notification.deepLink;

  switch (screen) {
    case "TransactionDetail":
    case "TradeDetail":
      navigate("/trading/digitalWallet");
      break;
    case "AutoStackDetail":
      if (params?.autoStackId) {
        navigate(`/autostack/${params.autoStackId}`);
      } else {
        navigate("/autostack");
      }
      break;
    case "Wallet":
      navigate("/settings/wallet");
      break;
    case "PriceAlerts":
      navigate("/settings/price-alerts");
      break;
    case "PayTransactionDetail":
      navigate("/");
      break;
    default:
      navigate("/");
  }
};
